import { ref } from 'vue'
import useMainStore from '@views/main/main.store'
import type { IncrementalMessage } from '@views/main/main.store'

/**
 * initIncrementalUpdate 初始化增量更新处理函数，增加消息储存机制
 *
 * 该函数创建了一个消息队列系统，用于管理增量更新消息的顺序处理。主要特性：
 * 1. 自动维护消息队列，保证先进先出（FIFO）处理顺序
 * 2. 防止并发处理，通过 isPending 状态标志确保同一时间只处理一个消息
 * 3. 支持自动触发和手动触发两种处理模式
 *
 * @param cb - 增量消息处理回调函数，类型为 (message: IncrementalMessage) => Promise<void>
 *             当队列开始处理消息时，会依次调用此回调处理每条消息
 *
 * @returns 返回包含以下属性的对象：
 *   - isPending: Ref<boolean> 当前处理状态（是否正在处理消息）
 *   - loadNextMessage: () => Promise<void> 手动触发处理下一条消息的方法
 *   - handleIncrementalMessage: (message: IncrementalMessage) => void 消息接收方法，将新消息加入队列
 *
 * @example
 * // 在组件中使用示例
 * const { handleIncrementalMessage } = initIncrementalUpdate(async (msg) => {
 *   // 处理增量消息的异步逻辑
 * });
 */
function initIncrementalUpdate(
  cb: (message: IncrementalMessage) => Promise<void>,
  autoLoad = true,
) {
  const mainStore = useMainStore()
  const messageList = ref<IncrementalMessage[]>([])
  const isPending = ref(false)
  const loadNextMessage = async () => {
    if (!messageList.value.length) return
    isPending.value = true
    const shiftItem = messageList.value.shift()
    // 移除已处理消息
    if (mainStore.incrementalMessageList[shiftItem.id])
      delete mainStore.incrementalMessageList[shiftItem.id]
    await cb(shiftItem)
    if (autoLoad) {
      isPending.value = false
      loadNextMessage()
    }
  }
  const handleIncrementalMessage = (message: IncrementalMessage | undefined) => {
    // 判断消息是否存在
    if (!message) return
    messageList.value.push(message)
    if (!isPending.value) loadNextMessage()
  }
  return {
    isPending,
    loadNextMessage,
    handleIncrementalMessage,
  }
}

export default initIncrementalUpdate
